import React, { useEffect, useRef } from 'react';

import { AlineTransforms, isRenderModelPageEntry, SelEventSource } from '@aline/common';
import type { Path, Point } from 'slate';
import { Range } from 'slate';
import { Queries } from 'src/editor/queries';
import { getEffectiveZoom } from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import { makeStyles } from 'tss-react/mui';

import { useEditor, useRangeRects, useSelection, useSlateSelector } from '../hooks';

const HANDLE_DOT_PX = 16;
const HANDLE_HIT_PX = 32;

const useStyles = makeStyles()(theme => ({
  /**
   * The visible dot is centered inside the larger transparent hit-box so the
   * user has a generous tap target without making the dot itself look heavy.
   */
  dot: {
    backgroundColor: theme.palette.primary.main,
    border: `2px solid ${theme.palette.common.white}`,
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
    height: HANDLE_DOT_PX,
    left: (HANDLE_HIT_PX - HANDLE_DOT_PX) / 2,
    pointerEvents: 'none',
    position: 'absolute',
    top: (HANDLE_HIT_PX - HANDLE_DOT_PX) / 2,
    width: HANDLE_DOT_PX,
  },
  /**
   * Sits below the modal so the field sheet always wins, but well above any
   * page content or in-document overlays.
   */
  hit: {
    height: HANDLE_HIT_PX,
    pointerEvents: 'auto',
    position: 'absolute',
    touchAction: 'none',

    width: HANDLE_HIT_PX,
    /**
     * iOS Firefox/Chrome (both WebKit) can skip repainting small absolute
     * descendants when an ancestor's `transform: scale` changes mid-pinch.
     * Promoting the handle to its own GPU layer forces an independent repaint
     * so the dots stay visible after pinch zoom.
     */
    willChange: 'transform',
    zIndex: theme.zIndex.modal - 2,
  },
}));

type Side = 'start' | 'end';

export function MobileSelectionHandles(): JSX.Element | null {
  const selection = useSelection();
  const rangeRects = useRangeRects(selection);
  const isGridSelection = useSlateSelector(editor => Queries.getIsGridSelection(editor));

  // Grid selections span whole cells and have no linear endpoints to attach drag handles to.
  if (!IS_MOBILE || !selection || isGridSelection || !Range.isExpanded(selection) || !rangeRects)
    return null;

  const { firstLineRect, lastLineRect } = rangeRects;

  return (
    <>
      <Handle
        side="start"
        x={firstLineRect.leftPx - HANDLE_HIT_PX / 2}
        y={firstLineRect.topPx - HANDLE_HIT_PX}
      />
      <Handle
        side="end"
        x={lastLineRect.leftPx + lastLineRect.widthPx - HANDLE_HIT_PX / 2}
        y={lastLineRect.topPx + lastLineRect.heightPx}
      />
    </>
  );
}

interface HandleProps {
  side: Side;
  x: number;
  y: number;
}

function Handle({ side, x, y }: HandleProps): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const hitRef = useRef<HTMLDivElement>(null);
  const otherPointRef = useRef<Point | null>(null);
  const pageElsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    const el = hitRef.current;
    if (!el) return;

    function updateSelectionFromPointer(clientX: number, clientY: number): void {
      const target = findPageAtPoint(clientX, clientY, pageElsRef.current);
      if (!target) return;

      const { pageEl, pagePath } = target;
      const rMPageEntry = Queries.renderModelNode(editor, pagePath);
      if (!isRenderModelPageEntry(rMPageEntry)) return;

      const rect = pageEl.getBoundingClientRect();
      const zoom = getEffectiveZoom(pageEl);
      const pointerX = (clientX - rect.left) / zoom;
      const pointerY = (clientY - rect.top) / zoom;

      const pointDetails = Queries.resolvePointerToPoint(editor, {
        isFooterOpen: false,
        isHeaderOpen: false,
        pointerX,
        pointerY,
        rMElementEntry: rMPageEntry,
      });
      if (!pointDetails) return;

      const otherPoint = otherPointRef.current;
      if (!otherPoint) return;

      /**
       * Dragged point goes in `focus`, pinned endpoint in `anchor` — the
       * editor scrolls to keep `focus` visible, so the viewport tracks the
       * finger instead of yanking back to the pinned end.
       */
      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.MOUSE,
        props: {
          affinity: pointDetails.affinity,
          anchor: otherPoint,
          focus: pointDetails.cMPoint,
        },
      });
    }

    /**
     * setPointerCapture re-targets move events to this element regardless of
     * where the finger drifts.
     */
    function onPointerDown(e: PointerEvent): void {
      if (!e.isPrimary) return;
      e.preventDefault();
      e.stopPropagation();
      el?.setPointerCapture(e.pointerId);
      const sel = editor.selection;
      if (sel) {
        const [start, end] = Range.edges(sel);
        otherPointRef.current = side === 'start' ? end : start;
      }
      pageElsRef.current = [...document.querySelectorAll<HTMLElement>('[data-page]')];
    }

    function onPointerMove(e: PointerEvent): void {
      if (!el?.hasPointerCapture(e.pointerId)) return;
      e.preventDefault();
      e.stopPropagation();
      updateSelectionFromPointer(e.clientX, e.clientY);
    }

    function onPointerUp(e: PointerEvent): void {
      if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      otherPointRef.current = null;
      pageElsRef.current = [];
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [editor, side]);

  /**
   * React's synthetic mouse events bubble even when the underlying pointer
   * sequence did not — stop them at the handle so a stray click can't reach
   * Page's selection handler and collapse the current range.
   */
  function stopMouse(e: React.SyntheticEvent): void {
    e.stopPropagation();
  }

  return (
    <div
      ref={hitRef}
      className={classes.hit}
      style={{ left: x, top: y }}
      onClick={stopMouse}
      onMouseDown={stopMouse}
      onMouseUp={stopMouse}
    >
      <div className={classes.dot} />
    </div>
  );
}

function findPageAtPoint(
  clientX: number,
  clientY: number,
  pageEls: HTMLElement[]
): { pageEl: HTMLElement; pagePath: Path } | null {
  for (const pageEl of pageEls) {
    const rect = pageEl.getBoundingClientRect();
    if (clientX < rect.left || clientX > rect.right) continue;
    if (clientY < rect.top || clientY > rect.bottom) continue;
    const rawPagePath = pageEl.dataset.pagePath;
    if (!rawPagePath) continue;
    const pagePath: Path = JSON.parse(rawPagePath);
    return { pageEl, pagePath };
  }
  return null;
}
